import React from "react";
import { addYears, format, isAfter, isBefore, startOfDay, subYears } from "date-fns";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

/** The date the annual deductible resets is sent as `firstDue`. It can lie anywhere between the
 * start of insurance and one year after it — one year after is the default (full first period). */
export const getDefaultDeductibleResetDate = (startOfInsurance: Date): Date =>
  startOfDay(addYears(startOfInsurance, 1));

/** Pulls a stored `firstDue` (e.g. from a loaded offer) into [start, start + 1 year] by shifting
 * it in whole years, mirroring how `ConfirmationPage` interprets it. */
export const clampDeductibleResetDate = (value: Date, startOfInsurance: Date): Date => {
  const start = startOfDay(startOfInsurance);
  const max = addYears(start, 1);
  let result = startOfDay(value);
  while (isBefore(result, start)) result = addYears(result, 1);
  while (isAfter(result, max)) result = subYears(result, 1);
  return result;
};

/** The date within (start, start + 1 year] that falls on the given day and month — the first one after the start, which
 * is why the start's own day and month give the default (start + 1 year). `null` for a day/month that does not exist. */
export const resolveDeductibleResetDate = (day: number, month: number, startOfInsurance: Date): Date | null => {
  const start = startOfDay(startOfInsurance);
  for (let year = start.getFullYear(); year <= start.getFullYear() + 4; year++) {
    const candidate = new Date(year, month - 1, day);
    if (candidate.getMonth() !== month - 1) continue; // e.g. 29.02. in a non-leap year
    if (isAfter(candidate, start) && !isAfter(candidate, addYears(start, 1))) return candidate;
  }
  return null;
};

interface DeductibleResetDateInputProps {
  /** `null` means "use the default" (start of insurance + 1 year). */
  firstDue: Date | null;
  startOfInsurance: Date;
  onChange: (date: Date | null) => void;
  disabled?: boolean;
  /** With no Jahresfranchise only the sums insured restart, so the label leaves it out. */
  hasDeductible?: boolean;
}

/** Broker funnel only: the day and month the sums insured and the Jahresfranchise restart, typed as "TT.MM" (no
 * calendar). The year follows from the start of insurance and is sent as `firstDue`. */
const DeductibleResetDateInput: React.FC<DeductibleResetDateInputProps> = ({
  firstDue,
  startOfInsurance,
  onChange,
  disabled,
  hasDeductible = true,
}) => {
  const { t } = useTranslation();

  const startTime = startOfDay(startOfInsurance).getTime();
  const min = React.useMemo(() => new Date(startTime), [startTime]);
  const max = React.useMemo(() => addYears(new Date(startTime), 1), [startTime]);

  const value = React.useMemo(
    () => (firstDue ? clampDeductibleResetDate(firstDue, min) : max),
    [firstDue, min, max]
  );

  // While typing, a field shows its draft; otherwise the day / month of the current value.
  const [draftDay, setDraftDay] = React.useState<string | null>(null);
  const [draftMonth, setDraftMonth] = React.useState<string | null>(null);
  const [error, setError] = React.useState(false);
  const groupRef = React.useRef<HTMLDivElement>(null);
  const monthRef = React.useRef<HTMLInputElement>(null);
  const dayRef = React.useRef<HTMLInputElement>(null);

  const dayText = draftDay ?? format(value, "dd");
  const monthText = draftMonth ?? format(value, "MM");

  const commit = (day: string, month: string) => {
    if (day.length < 2 || month.length < 2) {
      setError(false);
      return;
    }
    const resolved = resolveDeductibleResetDate(Number(day), Number(month), min);
    setError(!resolved);
    if (resolved) onChange(resolved.getTime() === max.getTime() ? null : resolved);
  };

  const handleDayChange = (raw: string) => {
    const day = raw.replace(/\D/g, "").slice(0, 2);
    setDraftDay(day);
    commit(day, monthText);
    if (day.length === 2) monthRef.current?.focus();
  };

  const handleMonthChange = (raw: string) => {
    const month = raw.replace(/\D/g, "").slice(0, 2);
    setDraftMonth(month);
    commit(dayText, month);
  };

  // Backspace in an empty month field steps back into the day field.
  const handleMonthKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && monthText === "") dayRef.current?.focus();
  };

  // A single digit ("5") is completed to "05" when the field is left, so "5 / 3" works without typing the zeros.
  const pad = (text: string) => (text.length === 1 ? `0${text}` : text);
  const handleGroupBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (groupRef.current?.contains(e.relatedTarget as Node | null)) return;
    // Only an actual edit is committed; leaving the fields untouched must not re-set the date.
    if (draftDay !== null || draftMonth !== null) commit(pad(dayText), pad(monthText));
    // An incomplete or invalid entry falls back to the last valid value instead of leaving the fields looking wrong.
    setDraftDay(null);
    setDraftMonth(null);
    setError(false);
  };

  // Safari places the caret on mouse-up and thereby drops the selection made on focus; with a full two-digit field
  // the next digits would then be rejected (maxLength), so the click must not move the caret.
  const keepSelection = (e: React.MouseEvent<HTMLInputElement>) => {
    if (e.currentTarget.selectionStart === 0 && e.currentTarget.selectionEnd === e.currentTarget.value.length) e.preventDefault();
  };

  const faqUrl = t("priceCustomization.deductibleResetDateFaqUrl");
  const fieldClass = cn("w-16 text-center", error && "border-destructive focus-visible:ring-destructive");

  return (
    <div className="space-y-2">
      <Label htmlFor="deductible-reset-day" className="text-lg font-medium">
        {t(hasDeductible ? "priceCustomization.deductibleResetDateBroker" : "priceCustomization.deductibleResetDateBrokerNoDeductible")}{" "}
        <a
          href={faqUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary text-base underline underline-offset-4 hover:opacity-80"
        >
          {t("priceCustomization.deductibleResetDateMore")}
        </a>
      </Label>
      <div ref={groupRef} className="flex items-center gap-2" onBlur={handleGroupBlur}>
        <Input
          id="deductible-reset-day"
          ref={dayRef}
          inputMode="numeric"
          autoComplete="off"
          placeholder={t("priceCustomization.deductibleResetDayPlaceholder")}
          aria-label={t("priceCustomization.deductibleResetDay")}
          maxLength={2}
          disabled={disabled}
          value={dayText}
          onChange={(e) => handleDayChange(e.target.value)}
          onFocus={(e) => e.target.select()}
          onMouseUp={keepSelection}
          aria-invalid={error}
          className={fieldClass}
        />
        <span aria-hidden="true" className="text-lg text-muted-foreground">/</span>
        <Input
          id="deductible-reset-month"
          ref={monthRef}
          inputMode="numeric"
          autoComplete="off"
          placeholder={t("priceCustomization.deductibleResetMonthPlaceholder")}
          aria-label={t("priceCustomization.deductibleResetMonth")}
          maxLength={2}
          disabled={disabled}
          value={monthText}
          onChange={(e) => handleMonthChange(e.target.value)}
          onKeyDown={handleMonthKeyDown}
          onFocus={(e) => e.target.select()}
          onMouseUp={keepSelection}
          aria-invalid={error}
          className={fieldClass}
        />
      </div>
      <p role={error ? "alert" : undefined} className={error ? "text-xs text-destructive" : "text-xs text-muted-primary"}>
        {error ? t("priceCustomization.deductibleResetDateInvalid") : t("priceCustomization.deductibleResetDateInputHint")}
      </p>
    </div>
  );
};

export default DeductibleResetDateInput;
